/**
 * Optional embed-dashboard attribution context, forwarded by the web client as
 * request headers on RunUdsSql calls. Purely observability metadata: it names
 * the dashboard a UDS query belongs to on the `uds.query` span and audit log
 * line, so slow queries attribute to a dashboard without SQL-text matching.
 * Never used for authorization — values are client-asserted.
 */
export type EmbedClientContext = {
  embedId?: string
  viewContentId?: string
}

export const EMBED_ID_HEADER = 'x-doss-embed-id'
export const VIEW_CONTENT_ID_HEADER = 'x-doss-view-content-id'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Parses the embed attribution headers from a request. Malformed values are
 * dropped silently (this is telemetry, not input); returns undefined when no
 * valid header is present so callers can omit the field entirely.
 */
export const parseEmbedClientContext = (
  getHeader: (name: string) => string | undefined,
): EmbedClientContext | undefined => {
  const rawEmbedId = getHeader(EMBED_ID_HEADER)
  const rawViewContentId = getHeader(VIEW_CONTENT_ID_HEADER)
  const embedId =
    rawEmbedId !== undefined && UUID_PATTERN.test(rawEmbedId) ? rawEmbedId.toLowerCase() : undefined
  const viewContentId =
    rawViewContentId !== undefined && UUID_PATTERN.test(rawViewContentId)
      ? rawViewContentId.toLowerCase()
      : undefined
  if (embedId === undefined && viewContentId === undefined) {
    return undefined
  }
  return {
    ...(embedId === undefined ? {} : { embedId }),
    ...(viewContentId === undefined ? {} : { viewContentId }),
  }
}
